import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Sparkles, Upload, Download, Send,
  MessageSquare, Image as ImageIcon, RefreshCw, Copy, Check, Wand2, Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { runToolJob } from "@/lib/toolJobs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRANSIENT_STATUS = new Set([502, 503, 504, 520, 521, 522, 523, 524, 408]);
async function _postWithRetry(url, data, config, { attempts = 3, baseDelayMs = 2000 } = {}) {
  let lastErr;
  for (let i = 1; i <= attempts; i++) {
    try { return await axios.post(url, data, config); }
    catch (err) {
      lastErr = err;
      const transient = !err.response || TRANSIENT_STATUS.has(err.response.status);
      if (!transient || i === attempts) throw err;
      await new Promise(r => setTimeout(r, baseDelayMs * i));
    }
  }
  throw lastErr;
}

const FORMATS = [
  { id: "4:5",  label: "Feed Post",  dims: "1080 × 1350", emoji: "▭" },
  { id: "1:1",  label: "Quadrat",    dims: "1080 × 1080", emoji: "■" },
  { id: "9:16", label: "Story/Reel", dims: "1080 × 1920", emoji: "▯" },
  { id: "16:9", label: "Landscape",  dims: "1920 × 1080", emoji: "▬" },
];

const RESOLUTIONS = [
  { id: "standard", label: "Standard", desc: "1080px" },
  { id: "hd",       label: "HD",       desc: "2K Upscale" },
  { id: "ultra",    label: "Ultra HD", desc: "4K Upscale" },
];

const CONTENT_TYPES = [
  { id: "carousel", label: "Karussell / Stories", desc: "6 Slides mit Creative Director Chat", icon: "slides" },
  { id: "titelbild", label: "Titelbild / Reel-Cover", desc: "1 einzelnes Bild mit Titelbild-Bot", icon: "image" },
];

export default function ContentGeneratorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [contentType, setContentType] = useState("carousel");
  const [mode, setMode] = useState("chat");

  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [hasSlides, setHasSlides] = useState(false);
  const [hasPrompt, setHasPrompt] = useState(false);
  const chatBottomRef = useRef(null);

  const [slidePromptsText, setSlidePromptsText] = useState("");

  const [fmt, setFmt] = useState("4:5");
  const [resolution, setResolution] = useState("standard");

  const [refUrl, setRefUrl] = useState("");
  const [uploadingRef, setUploadingRef] = useState(false);
  const refFileInput = useRef(null);

  const [designUrl, setDesignUrl] = useState("");
  const [uploadingDesign, setUploadingDesign] = useState(false);
  const designFileInput = useRef(null);

  const [designDescription, setDesignDescription] = useState("");

  const [rendering, setRendering] = useState(false);
  const [renderProgress, setRenderProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [jobIdState, setJobIdState] = useState("");

  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [captionPlatform, setCaptionPlatform] = useState("instagram");
  const [captionLoading, setCaptionLoading] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/carousel/me`)
      .then(r => { if (r.data?.reference_photo_url) setRefUrl(r.data.reference_photo_url); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const jobId = searchParams.get("jobId");
    if (!jobId) return;
    (async () => {
      try {
        const { data: job } = await axios.get(`${API}/tools/jobs/${jobId}`);
        if (job?.result?.slides?.length) {
          setResult(job.result);
          setJobIdState(jobId);
          if (job.result.format) setFmt(job.result.format);
          if (job.input?.slide_prompts?.length) {
            const reconstructed = job.input.slide_prompts
              .map((p, i) => `Slide ${i + 1}:\n${p}`)
              .join("\n\n");
            setSlidePromptsText(reconstructed);
          }
          if (job.input?.reference_image_url) setRefUrl(job.input.reference_image_url);
          if (job.input?.design_reference_url) setDesignUrl(job.input.design_reference_url);
          toast.success(`Karussell wiederhergestellt · ${job.result.slides.length} Slides`);
        } else if (job?.status === "failed") {
          toast.error("Dieses Karussell ist leider fehlgeschlagen.");
        } else if (job?.status === "running") {
          toast.info("Dieses Karussell läuft noch. Schau gleich nochmal rein.");
        }
      } catch (_) { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chatBottomRef.current) chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const switchContentType = (type) => {
    if (type === contentType) return;
    setContentType(type);
    setMessages([]);
    setSessionId("");
    setHasSlides(false);
    setHasPrompt(false);
    setSlidePromptsText("");
    setResult(null);
    setJobIdState("");
    setCaption("");
    setHashtags([]);
  };

  const sendChatMessage = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    setChatInput("");
    const userMsg = { role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setChatLoading(true);
    try {
      const endpoint = contentType === "carousel"
        ? `${API}/carousel/master-chat`
        : `${API}/content/titelbild-chat`;
      const { data } = await _postWithRetry(endpoint, {
        message: text, session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages(prev => [...prev, data.assistant_message]);
      if (contentType === "carousel") {
        setHasSlides(!!data.has_slides);
        if (data.has_slides) {
          setSlidePromptsText(data.assistant_message.content);
          toast.success("Slide-Prompts erzeugt! Sie wurden unten ins Textfeld kopiert.");
        }
      } else {
        setHasPrompt(!!data.has_prompt);
        if (data.has_prompt) {
          setSlidePromptsText(data.assistant_message.content);
          toast.success("Bild-Prompt erzeugt! Er wurde unten ins Textfeld kopiert.");
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chat-Fehler");
      setMessages(prev => prev.slice(0, -1));
      setChatInput(text);
    } finally {
      setChatLoading(false);
    }
  };

  const resetChat = async () => {
    const endpoint = contentType === "carousel"
      ? `${API}/carousel/master-chat/${sessionId}/reset`
      : `${API}/content/titelbild-chat/${sessionId}/reset`;
    if (sessionId) {
      try { await axios.post(endpoint); } catch (_) {}
    }
    setSessionId("");
    setMessages([]);
    setHasSlides(false);
    setHasPrompt(false);
    setSlidePromptsText("");
  };

  const uploadFile = async (file, endpoint, setUrl, setUploadFlag, label) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("Datei zu groß (max 12 MB)"); return; }
    setUploadFlag(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await _postWithRetry(`${API}${endpoint}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const url = data.reference_photo_url || data.style_reference_url;
      setUrl(url);
      toast.success(`${label} hochgeladen!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally { setUploadFlag(false); }
  };

  const handleGenerate = async () => {
    let promptsText = slidePromptsText.trim();
    if (contentType === "carousel" && mode === "design") {
      if (!designUrl) { toast.error("Bitte lade eine Design-Vorlage hoch."); return; }
      if (!promptsText && !designDescription.trim()) {
        toast.error("Beschreibe kurz, was im Karussell passieren soll.");
        return;
      }
      if (!promptsText) {
        promptsText = `Slide 1:\n${designDescription.trim()} — replicate the exact layout, typography and composition from the attached design reference. Slide 1 of 3.\n\nSlide 2:\n${designDescription.trim()} — same visual style as the design reference, slide 2 of 3 (variant pose / scene).\n\nSlide 3:\n${designDescription.trim()} — same visual style as the design reference, final slide 3 of 3 with CTA emphasized.`;
      }
    } else if (contentType === "titelbild") {
      if (!promptsText) {
        toast.error("Kein Bild-Prompt vorhanden. Beende erst den Chat oder schreibe ihn selbst.");
        return;
      }
      if (!/^\s*slide\s*\d/im.test(promptsText)) {
        promptsText = `Slide 1:\n${promptsText}`;
      }
    } else {
      if (!promptsText) {
        toast.error("Keine Slide-Prompts. Beende erst den Chat oder schreibe sie selbst.");
        return;
      }
    }

    setRendering(true);
    setResult(null);
    setRenderProgress({ elapsedSec: 0, status: "running" });
    try {
      const data = await runToolJob("post", "/carousel/render-master", {
        slide_prompts_text: promptsText,
        fmt,
        resolution,
        reference_image_url: refUrl,
        design_reference_url: designUrl,
      }, {
        onTick: ({ elapsedSec, status }) => setRenderProgress({ elapsedSec, status }),
        etaSec: 50, intervalMs: 4000, maxWaitMs: 600000,
      });
      const jobIdFromData = data.__jobId || null;
      setResult(data);
      setJobIdState(jobIdFromData || "");
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.slides?.length || 0} von ${(data.slides?.length || 0) + data.errors.length} Slides erfolgreich`);
      } else {
        const label = contentType === "titelbild" ? "Titelbild" : `${data.slides?.length || 0} Slides`;
        toast.success(`${label} erstellt!`);
      }
    } catch (e) {
      if (e.code === "POLL_LOST_BACKGROUND_RUNNING" || e.code === "POLL_DEADLINE_BACKGROUND_RUNNING") {
        if (e.jobId) setJobIdState(e.jobId);
        toast.info(e.message, { duration: 14000 });
      } else {
        toast.error(e.message || "Fehler bei der Generierung");
      }
    } finally {
      setRendering(false);
      setRenderProgress(null);
    }
  };

  const generateCaption = async () => {
    if (!jobIdState) { toast.error("Erst generieren, dann Caption erzeugen."); return; }
    setCaptionLoading(true);
    setCaptionCopied(false);
    try {
      const { data } = await axios.post(`${API}/carousel/generate-caption`, {
        job_id: jobIdState,
        platform: captionPlatform,
      });
      setCaption(data.caption || "");
      setHashtags(Array.isArray(data.hashtags) ? data.hashtags : []);
      toast.success("Caption erzeugt");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caption-Generierung fehlgeschlagen");
    } finally {
      setCaptionLoading(false);
    }
  };

  const copyCaption = async () => {
    const tagsLine = hashtags.length ? "\n\n" + hashtags.join(" ") : "";
    const text = (caption || "") + tagsLine;
    try {
      await navigator.clipboard.writeText(text);
      setCaptionCopied(true);
      toast.success("In die Zwischenablage kopiert");
      setTimeout(() => setCaptionCopied(false), 1500);
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const downloadSlide = (slide) => {
    if (!jobIdState) {
      toast.error("Download nicht möglich — bitte 2 Sekunden warten.");
      return;
    }
    const url = `${API}/carousel/download/${jobIdState}/${slide.n}`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadAll = () => {
    if (!jobIdState) {
      toast.error("Download nicht möglich — bitte 2 Sekunden warten.");
      return;
    }
    const url = `${API}/carousel/download/${jobIdState}/all`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("ZIP-Download gestartet");
  };

  const chatReady = contentType === "carousel" ? hasSlides : hasPrompt;
  const chatLabel = contentType === "carousel" ? "Creative Director Chat" : "Titelbild-Bot Chat";
  const chatPlaceholder = contentType === "carousel"
    ? "Schreibe was du machen m\u00f6chtest (z.B. \u201eKarussell zum Thema Online-Sichtbarkeit\u201c)"
    : "Beschreibe dein Titelbild (z.B. \u201eReel-Cover f\u00fcr einen Fitness-Coach, dunkel und modern\u201c)";

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="content-generator-page">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}
                    className="h-8 w-8 text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Sparkles className="w-5 h-5" style={{ color: '#00D4FF' }} />
            <div>
              <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Content Generator
              </h1>
              <p className="text-[11px] text-zinc-500">Karussell-Slides, Titelbilder & Reel-Cover mit KI</p>
            </div>
          </div>
          <button onClick={() => navigate('/analyses')}
                  className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
            {"↻"} meine Ergebnisse
          </button>
        </div>

        {/* Content Type Selection */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          {CONTENT_TYPES.map(ct => (
            <button key={ct.id} onClick={() => switchContentType(ct.id)}
                    className={`p-4 rounded-xl border text-left transition-all ${contentType === ct.id ? 'border-cyan-500 bg-cyan-500/5' : 'border-zinc-800 hover:border-zinc-700'}`}>
              <div className="flex items-center gap-2 mb-1.5">
                {ct.icon === "slides"
                  ? <Layers className="w-4 h-4" style={{ color: contentType === ct.id ? '#00D4FF' : '#9A9A9F' }} />
                  : <ImageIcon className="w-4 h-4" style={{ color: contentType === ct.id ? '#00D4FF' : '#9A9A9F' }} />}
                <p className={`text-sm font-semibold ${contentType === ct.id ? 'text-white' : 'text-zinc-300'}`}>{ct.label}</p>
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed">{ct.desc}</p>
            </button>
          ))}
        </div>

        {/* Mode toggle (only for carousel) */}
        {contentType === "carousel" && (
          <div className="grid grid-cols-2 gap-2 mb-6">
            <button onClick={() => setMode("chat")}
                    className={`p-3 rounded-xl border text-left transition-all ${mode === "chat" ? 'border-cyan-500 bg-cyan-500/5' : 'border-zinc-800 hover:border-zinc-700'}`}>
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare className="w-4 h-4" style={{ color: mode === 'chat' ? '#00D4FF' : '#9A9A9F' }} />
                <p className={`text-sm font-semibold ${mode === 'chat' ? 'text-white' : 'text-zinc-300'}`}>Geführter Chat</p>
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ml-auto"
                      style={{ background: 'rgba(0,212,255,0.12)', color: '#00D4FF' }}>EMPFOHLEN</span>
              </div>
              <p className="text-[11px] text-zinc-500">Creative Director stellt gezielte Fragen, baut perfekte Slide-Prompts.</p>
            </button>
            <button onClick={() => setMode("design")}
                    className={`p-3 rounded-xl border text-left transition-all ${mode === "design" ? 'border-cyan-500 bg-cyan-500/5' : 'border-zinc-800 hover:border-zinc-700'}`}>
              <div className="flex items-center gap-2 mb-1">
                <ImageIcon className="w-4 h-4" style={{ color: mode === 'design' ? '#00D4FF' : '#9A9A9F' }} />
                <p className={`text-sm font-semibold ${mode === 'design' ? 'text-white' : 'text-zinc-300'}`}>Ich habe ein Design</p>
              </div>
              <p className="text-[11px] text-zinc-500">Design-Vorlage hochladen, Thema beschreiben, fertig.</p>
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* LEFT: Chat or Design upload */}
          <div className="lg:col-span-3 space-y-3">
            {(contentType === "titelbild" || mode === "chat") && (
              <div className="rounded-xl bg-zinc-900/40 border border-zinc-800 flex flex-col" style={{ height: '520px' }}>
                <div className="flex items-center justify-between p-3 border-b border-zinc-800">
                  <p className="text-xs font-semibold text-white">{"\uD83D\uDCAC"} {chatLabel}</p>
                  {messages.length > 0 && (
                    <button onClick={resetChat} className="text-[11px] text-zinc-400 hover:text-white flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" /> neu starten
                    </button>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
                  {messages.length === 0 && (
                    <div className="p-3 rounded-lg" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)' }}>
                      <p className="text-xs text-zinc-300 leading-relaxed">
                        {"\uD83D\uDC4B"} {contentType === "carousel"
                          ? "Schreib einfach was du machen m\u00f6chtest (z.B. \u201eKarussell zum Thema Online-Sichtbarkeit\u201c). Der Creative Director stellt dir gezielte Fragen, dann baut er perfekte Slide-Prompts f\u00fcr dich."
                          : "Beschreibe dein Titelbild oder Reel-Cover. Der Bot stellt dir 2-3 kurze Fragen und baut dann den perfekten Bild-Prompt."}
                      </p>
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-cyan-500/15 text-white border border-cyan-500/30" : "bg-zinc-950 text-zinc-200 border border-zinc-800"}`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2">
                        <Loader2 className="w-3 h-3 animate-spin text-zinc-500" />
                      </div>
                    </div>
                  )}
                  <div ref={chatBottomRef} />
                </div>
                <div className="p-3 border-t border-zinc-800 flex gap-2">
                  <Input value={chatInput}
                         onChange={e => setChatInput(e.target.value)}
                         onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                         placeholder={chatReady ? "Noch Änderungen? Sonst auf 'Generieren' klicken." : chatPlaceholder}
                         className="bg-zinc-950 border-zinc-800 text-white text-xs h-9 flex-1"
                         disabled={chatLoading} />
                  <Button onClick={sendChatMessage} disabled={!chatInput.trim() || chatLoading}
                          size="sm" className="h-9 px-3"
                          style={{ background: '#00D4FF', color: '#0a0a0f' }}>
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}

            {contentType === "carousel" && mode === "design" && (
              <div className="rounded-xl bg-zinc-900/40 border border-zinc-800 p-4 space-y-3">
                <p className="text-xs font-semibold text-white flex items-center gap-1.5">
                  <ImageIcon className="w-4 h-4" style={{ color: '#00D4FF' }} /> Design-Vorlage hochladen
                </p>
                {designUrl ? (
                  <div className="relative rounded-lg overflow-hidden border border-zinc-700">
                    <img src={designUrl} alt="Design Referenz" className="w-full max-h-64 object-contain bg-zinc-950" />
                    <button onClick={() => setDesignUrl("")}
                            className="absolute top-2 right-2 px-2.5 py-1 rounded bg-zinc-900/80 backdrop-blur text-[10px] text-white border border-zinc-700">
                      Entfernen
                    </button>
                  </div>
                ) : (
                  <div onClick={() => designFileInput.current?.click()}
                       className="cursor-pointer rounded-lg border-2 border-dashed border-zinc-700 hover:border-cyan-500/50 p-6 text-center transition-colors">
                    {uploadingDesign
                      ? <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1.5" style={{ color: '#00D4FF' }} />
                      : <Upload className="w-5 h-5 mx-auto mb-1.5" style={{ color: '#00D4FF' }} />}
                    <p className="text-xs font-medium text-white">{uploadingDesign ? "Lädt hoch..." : "Vorlage hochladen"}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">Canva, Pinterest, Screenshot · JPG/PNG · Max 12 MB</p>
                  </div>
                )}
                <input ref={designFileInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                       onChange={(e) => { const f = e.target.files?.[0]; uploadFile(f, "/carousel/upload-style-reference", setDesignUrl, setUploadingDesign, "Design"); e.target.value = ""; }} />
                <div>
                  <Label className="text-xs text-zinc-400 mb-1.5 block">Thema / Inhalt für das Karussell</Label>
                  <Textarea value={designDescription} onChange={e => setDesignDescription(e.target.value)}
                            placeholder="z.B.: 3 Tipps wie man als Coach mehr Klienten gewinnt."
                            className="bg-zinc-950 border-zinc-800 text-white text-xs min-h-[120px]" />
                  <p className="text-[10px] text-zinc-500 mt-1">Du kannst auch unten direkt strukturierte Slide-Prompts eingeben.</p>
                </div>
              </div>
            )}

            {/* Prompts textarea */}
            <div className="rounded-xl bg-zinc-900/40 border border-zinc-800 p-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-white flex items-center gap-1.5">
                  <Wand2 className="w-3.5 h-3.5" style={{ color: '#C8FF4D' }} />
                  {contentType === "carousel" ? "Slide-Prompts" : "Bild-Prompt"}
                </Label>
                {slidePromptsText && contentType === "carousel" && (
                  <span className="text-[10px] text-zinc-500">
                    {(slidePromptsText.match(/slide\s*\d/gim) || []).length} Slides erkannt
                  </span>
                )}
              </div>
              <Textarea value={slidePromptsText} onChange={e => setSlidePromptsText(e.target.value)}
                        placeholder={contentType === "carousel"
                          ? "Wird automatisch ausgefüllt sobald der Chat fertig ist. Du kannst danach noch editieren."
                          : "Wird automatisch ausgefüllt sobald der Chat fertig ist."}
                        className="bg-zinc-950 border-zinc-800 text-white text-[11px] font-mono min-h-[160px] leading-relaxed" />
              <p className="text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
                {contentType === "carousel"
                  ? "Jeder Slide beginnt mit \u201eSlide 1:\u201c, \u201eSlide 2:\u201c usw."
                  : "Der Prompt wird direkt an die Bildgenerierung gesendet."}
              </p>
            </div>
          </div>

          {/* RIGHT: Settings + reference + generate */}
          <div className="lg:col-span-2 space-y-3">
            {/* Format */}
            <div className="rounded-xl bg-zinc-900/40 border border-zinc-800 p-4">
              <Label className="text-xs font-semibold text-white mb-2 block">{"\uD83D\uDCD0"} Format</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {FORMATS.map(f => (
                  <button key={f.id} onClick={() => setFmt(f.id)}
                          className={`p-2 rounded-md text-left border text-xs transition-all ${fmt === f.id ? 'border-cyan-500 bg-cyan-500/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-base leading-none">{f.emoji}</span>
                      <p className={`font-semibold ${fmt === f.id ? 'text-white' : 'text-zinc-300'}`}>{f.label}</p>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{f.id} {"·"} {f.dims}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Resolution */}
            <div className="rounded-xl bg-zinc-900/40 border border-zinc-800 p-4">
              <Label className="text-xs font-semibold text-white mb-2 block">{"✨"} Auflösung</Label>
              <div className="flex gap-1.5">
                {RESOLUTIONS.map(r => (
                  <button key={r.id} onClick={() => setResolution(r.id)}
                          className={`flex-1 p-2 rounded-md text-center border text-[11px] transition-all ${resolution === r.id ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-zinc-800 hover:border-zinc-700 text-zinc-400'}`}>
                    <p className="font-semibold">{r.label}</p>
                    <p className="text-[9px] text-zinc-500 mt-0.5">{r.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Subject reference photo */}
            <div className="rounded-xl bg-zinc-900/40 border border-zinc-800 p-4">
              <Label className="text-xs font-semibold text-white mb-2 block flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5" /> Eigenes Foto (Avatar) <span className="text-zinc-500 font-normal">optional</span>
              </Label>
              {refUrl ? (
                <div className="relative rounded-lg overflow-hidden border border-zinc-700">
                  <img src={refUrl} alt="Avatar" className="w-full max-h-32 object-cover" />
                  <div className="absolute top-1.5 right-1.5 flex gap-1">
                    <button onClick={() => refFileInput.current?.click()}
                            className="px-2 py-0.5 rounded bg-zinc-900/80 backdrop-blur text-[10px] text-white border border-zinc-700 hover:bg-zinc-800">{"Ändern"}</button>
                    <button onClick={() => { setRefUrl(""); toast.success("Referenzbild entfernt"); }}
                            className="w-6 h-6 flex items-center justify-center rounded bg-zinc-900/80 backdrop-blur text-white border border-zinc-700 hover:bg-red-600 hover:border-red-600 transition-colors"
                            title="Referenzbild entfernen"
                            aria-label="Referenzbild entfernen">{"×"}</button>
                  </div>
                </div>
              ) : (
                <div onClick={() => refFileInput.current?.click()}
                     className="cursor-pointer rounded-lg border-2 border-dashed border-zinc-700 hover:border-cyan-500/50 p-3 text-center transition-colors">
                  {uploadingRef
                    ? <Loader2 className="w-4 h-4 animate-spin mx-auto" style={{ color: '#00D4FF' }} />
                    : <Upload className="w-4 h-4 mx-auto" style={{ color: '#00D4FF' }} />}
                  <p className="text-[11px] mt-1 text-zinc-300">{uploadingRef ? "Lädt..." : "Selfie/Avatar hochladen"}</p>
                </div>
              )}
              <input ref={refFileInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                     onChange={(e) => { const f = e.target.files?.[0]; uploadFile(f, "/carousel/upload-reference", setRefUrl, setUploadingRef, "Selfie"); e.target.value = ""; }} />
            </div>

            {/* Generate button */}
            <Button onClick={handleGenerate} disabled={rendering}
                    className="w-full text-black font-bold h-12 text-sm"
                    style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }}>
              {rendering
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generiere... ({renderProgress?.elapsedSec || 0}s)</>
                : <><Sparkles className="w-4 h-4 mr-2" />{contentType === "carousel" ? "Karussell generieren" : "Titelbild generieren"}</>}
            </Button>
            <p className="text-[10px] text-zinc-500 text-center">Dauer: ca. 30-60s {"·"} Format {fmt}</p>
          </div>
        </div>

        {/* Results */}
        {result?.slides?.length > 0 && (
          <div className="mt-8 rounded-xl bg-zinc-900/40 border border-zinc-800 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-white">{"✨"} {contentType === "carousel" ? `Dein Karussell (${result.slides.length} Slides ${"·"} ${result.format})` : `Dein Titelbild (${result.format})`}</p>
              {result.slides.length > 1 && (
                <button onClick={downloadAll}
                        className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5"
                        style={{ background: '#00D4FF', color: '#0a0a0f' }}>
                  <Download className="w-3.5 h-3.5" /> Alle als ZIP
                </button>
              )}
            </div>
            <div className={`grid gap-3 ${result.slides.length === 1 ? 'grid-cols-1 max-w-md' : fmt === '4:5' || fmt === '9:16' ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'}`}>
              {result.slides.map(s => (
                <div key={s.n} className="relative rounded-lg overflow-hidden border border-zinc-800 group">
                  <img src={`data:image/png;base64,${s.png_b64}`} alt={`Slide ${s.n}`} className="w-full" />
                  {result.slides.length > 1 && (
                    <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-zinc-900/80 backdrop-blur text-[10px] font-mono text-white">
                      {s.n.toString().padStart(2,'0')}/{result.slides.length.toString().padStart(2,'0')}
                    </div>
                  )}
                  <button onClick={() => downloadSlide(s)}
                          className="absolute bottom-1.5 right-1.5 p-1.5 rounded bg-zinc-900/80 backdrop-blur opacity-0 group-hover:opacity-100 transition-opacity">
                    <Download className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
              ))}
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="mt-3 p-2 rounded text-[10px] text-amber-400 bg-amber-500/5 border border-amber-500/20">
                <strong>Hinweis:</strong> {result.errors.length} Slide(s) konnten nicht erzeugt werden: {result.errors[0]}
              </div>
            )}

            {/* Caption + Hashtags Generator */}
            <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/40 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="text-xs font-semibold text-white flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" style={{ color: '#C8FF4D' }} />
                  Caption &amp; Hashtags <span className="text-zinc-500 font-normal">optional</span>
                </p>
                <div className="flex items-center gap-1.5">
                  <select value={captionPlatform} onChange={e => setCaptionPlatform(e.target.value)}
                          className="bg-zinc-900 border border-zinc-800 rounded text-[11px] text-white px-2 py-1 outline-none">
                    <option value="instagram">Instagram</option>
                    <option value="tiktok">TikTok</option>
                    <option value="linkedin">LinkedIn</option>
                  </select>
                  <button onClick={generateCaption} disabled={captionLoading || !jobIdState}
                          className="text-[11px] px-2.5 py-1 rounded-md font-semibold flex items-center gap-1 disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)', color: '#0a0a0f' }}>
                    {captionLoading
                      ? <><Loader2 className="w-3 h-3 animate-spin" />Generiere...</>
                      : <><Wand2 className="w-3 h-3" />{caption ? 'Neu generieren' : 'Caption generieren'}</>}
                  </button>
                </div>
              </div>
              {!caption && !captionLoading && (
                <p className="text-[10px] text-zinc-500">
                  Erzeugt eine passende deutsche Caption mit Hashtags {"—"} einfach kopieren und unter dem Beitrag einfügen.
                </p>
              )}
              {caption && (
                <div className="space-y-2">
                  <Textarea value={caption + (hashtags.length ? "\n\n" + hashtags.join(" ") : "")}
                            onChange={e => { setCaption(e.target.value); setHashtags([]); }}
                            className="bg-zinc-950 border-zinc-800 text-white text-xs min-h-[160px] whitespace-pre-wrap" />
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] text-zinc-500">
                      {hashtags.length > 0 ? `${hashtags.length} Hashtags` : 'Hashtags inline editierbar'} {"·"} Plattform: {captionPlatform}
                    </p>
                    <button onClick={copyCaption}
                            className="text-[11px] px-2.5 py-1 rounded-md border border-zinc-700 text-white hover:bg-zinc-800 flex items-center gap-1">
                      {captionCopied ? <><Check className="w-3 h-3" />Kopiert</> : <><Copy className="w-3 h-3" />Kopieren</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
